/* eslint-disable max-lines */
import React from 'react'
import { tagAxesObject, TagAxisId, excludeTags, flatOtherTags, extractDetailedTag } from '@cpsy-rmap-project/tags'
import { CrossRefPaper } from '@cpsy-rmap-project/web-core/crossref'
import Box from '@material-ui/core/Box'
import blue from '@material-ui/core/colors/blue'
import brown from '@material-ui/core/colors/brown'
import grey from '@material-ui/core/colors/grey'
import orange from '@material-ui/core/colors/orange'
import purple from '@material-ui/core/colors/purple'
import teal from '@material-ui/core/colors/teal'
import yellow from '@material-ui/core/colors/yellow'
import Divider from '@material-ui/core/Divider'
import Grid from '@material-ui/core/Grid'
import Link from '@material-ui/core/Link'
import Typography from '@material-ui/core/Typography'
import { useUnmount } from 'react-use'
import styled from 'styled-components'
import { customTheme } from '../../../libraries/MaterialUI'
import CustomClip from '../../atoms/CustomClip'
import HelpPopover from '../../atoms/HelpPopover'
import { SupplementBox } from './common'
import {
  CompMathMethodExperimentalDesignHelpText,
  UnitOfAnalysisHelpText,
  Dsm5HelpText,
  SymptomatologyHelpText,
  RdocConstructsHelpText
} from './helpText'

const Root = styled(Box)`
  margin: ${customTheme.spacing(0, 1)};
`

const CustomDivider = styled(Divider)`
  margin: ${customTheme.spacing(5, 0, 2, 0)} !important;
`

const getColor = (index: number) => {
  switch (index) {
    case 0:
      return `${purple[50]}77`
    case 1:
      return `${blue[50]}77`
    case 2:
      return `${yellow[50]}77`
    case 3:
      return `${teal[50]}77`
    case 4:
      return `${orange[50]}77`
    case 5:
      return `${brown[50]}77`
    default:
      return '#ffffff'
  }
}

const CognitiveProcessText: React.FC = () => (
  <Typography variant="caption" color="primary" component="span">
    {'Does this paper focus on cognitive processes rather than a psychiatric disease? Yes -> '}
  </Typography>
)

interface SelectorProps {
  tagAxisId: TagAxisId
  paper: CrossRefPaper
  handleDetailsChange: (update: Partial<CrossRefPaper>) => void
  helpPopoverComponent?: React.ReactNode
}

const Selector3Column: React.FC<SelectorProps> = ({ tagAxisId, paper, handleDetailsChange, helpPopoverComponent }) => {
  const tagAxis = tagAxesObject[tagAxisId]
  const selectedTags: string[] = paper[tagAxisId]

  const tags = excludeTags(tagAxis.tags, ['Unspecified', 'Cognitive process'])
  const cognitiveProcessTag = extractDetailedTag(tagAxisId, 'Cognitive process')

  return (
    <Root>
      <Typography variant="h4" gutterBottom>
        {tagAxis.name}
        {helpPopoverComponent}
      </Typography>

      <Grid
        container
        style={{
          backgroundColor: `${grey[200]}77`,
          border: 'solid 1px #00000022'
        }}
      >
        <Grid item xs={4}>
          <Typography variant="subtitle2" align="center">
            Domain (lv.1)
          </Typography>
        </Grid>
        <Grid item xs={4} style={{ borderLeft: 'solid 1px #00000022' }}>
          <Typography variant="subtitle2" align="center">
            Construct (lv.2)
          </Typography>
        </Grid>
        <Grid item xs={4} style={{ borderLeft: 'solid 1px #00000022' }}>
          <Typography variant="subtitle2" align="center">
            Subconstruct (lv.3)
          </Typography>
        </Grid>
      </Grid>

      {tags.map((tag, index) => (
        <Grid
          key={tag.id}
          container
          style={{
            backgroundColor: getColor(index),
            border: 'solid 1px #00000022'
          }}
        >
          <Grid item xs={4}>
            <CustomClip
              key={tag.id}
              tag={tag}
              selectedTags={selectedTags}
              handleDetailsChange={handleDetailsChange}
              selectedTagKey={tagAxis.id}
            />
          </Grid>
          <Grid item xs={8}>
            {tag.children?.map((cTag, cTagIndex) => {
              const isLast = tag.children && cTagIndex === tag.children.length - 1
              return (
                <Grid key={cTag.id} container style={isLast ? {} : { borderBottom: 'solid 1px #00000022' }}>
                  <Grid item xs={6} style={{ borderLeft: 'solid 1px #00000022' }}>
                    <CustomClip
                      key={cTag.id}
                      tag={cTag}
                      selectedTags={selectedTags}
                      handleDetailsChange={handleDetailsChange}
                      selectedTagKey={tagAxis.id}
                    />
                  </Grid>
                  <Grid item xs={6} style={{ borderLeft: 'solid 1px #00000022' }}>
                    {cTag.children?.map(gcTag => (
                      <CustomClip
                        key={gcTag.id}
                        tag={gcTag}
                        selectedTags={selectedTags}
                        handleDetailsChange={handleDetailsChange}
                        selectedTagKey={tagAxis.id}
                      />
                    ))}
                  </Grid>
                </Grid>
              )
            })}
          </Grid>
        </Grid>
      ))}

      {cognitiveProcessTag && (
        <Typography variant="caption" color="primary" component="div" style={{ margin: '10px' }}>
          <CognitiveProcessText />
          <CustomClip
            key={cognitiveProcessTag.id}
            tag={cognitiveProcessTag}
            selectedTags={selectedTags}
            handleDetailsChange={handleDetailsChange}
            selectedTagKey={tagAxis.id}
          />
        </Typography>
      )}
    </Root>
  )
}

const Selector2Column: React.FC<SelectorProps> = ({ tagAxisId, paper, handleDetailsChange, helpPopoverComponent }) => {
  const tagAxis = tagAxesObject[tagAxisId]
  const selectedTags: string[] = paper[tagAxisId]

  const tags = excludeTags(tagAxis.tags, ['Unspecified', 'Cognitive process'])
  const cognitiveProcessTag = extractDetailedTag(tagAxisId, 'Cognitive process')

  return (
    <Root>
      <Typography variant="h4" gutterBottom>
        {tagAxis.name}
        {helpPopoverComponent}
      </Typography>

      <Grid
        container
        style={{
          backgroundColor: `${grey[200]}77`,
          border: 'solid 1px #00000022'
        }}
      >
        <Grid item xs={6}>
          <Typography variant="subtitle2" align="center">
            Catergory
          </Typography>
        </Grid>
        <Grid item xs={6} style={{ borderLeft: 'solid 1px #00000022' }}>
          <Typography variant="subtitle2" align="center">
            Subcatergory
          </Typography>
        </Grid>
      </Grid>

      {tags.map((tag, index) => (
        <Grid
          key={tag.id}
          container
          style={{
            backgroundColor: getColor(index),
            border: 'solid 1px #00000022'
          }}
        >
          <Grid item xs={6}>
            <CustomClip
              key={tag.id}
              tag={tag}
              selectedTags={selectedTags}
              handleDetailsChange={handleDetailsChange}
              selectedTagKey={tagAxis.id}
            />
          </Grid>
          <Grid item xs={6} style={{ borderLeft: 'solid 1px #00000022' }}>
            {tag.children?.map(cTag => (
              <CustomClip
                key={cTag.id}
                tag={cTag}
                selectedTags={selectedTags}
                handleDetailsChange={handleDetailsChange}
                selectedTagKey={tagAxis.id}
              />
            ))}
          </Grid>
        </Grid>
      ))}

      {cognitiveProcessTag && (
        <Typography variant="caption" component="div" style={{ margin: '10px' }}>
          <CognitiveProcessText />
          <CustomClip
            key={cognitiveProcessTag.id}
            tag={cognitiveProcessTag}
            selectedTags={selectedTags}
            handleDetailsChange={handleDetailsChange}
            selectedTagKey={tagAxis.id}
          />
        </Typography>
      )}
    </Root>
  )
}

const Selector1Column: React.FC<SelectorProps> = ({ tagAxisId, paper, handleDetailsChange, helpPopoverComponent }) => {
  const tagAxis = tagAxesObject[tagAxisId]
  const selectedTags: string[] = paper[tagAxisId]

  const tags = excludeTags(flatOtherTags(tagAxis.tags), ['Unspecified', 'Cognitive process'])
  const cognitiveProcessTag = extractDetailedTag(tagAxisId, 'Cognitive process')

  return (
    <Root>
      <Typography variant="h4" gutterBottom>
        {tagAxis.name}
        {helpPopoverComponent}
      </Typography>

      <Grid container>
        <Grid item xs={12}>
          {tags.map(tag => (
            <CustomClip
              key={tag.id}
              tag={tag}
              selectedTags={selectedTags}
              handleDetailsChange={handleDetailsChange}
              selectedTagKey={tagAxis.id}
            />
          ))}
        </Grid>
      </Grid>

      {cognitiveProcessTag && (
        <Typography variant="caption" component="div" style={{ margin: '10px' }}>
          <CognitiveProcessText />
          <CustomClip
            key={cognitiveProcessTag.id}
            tag={cognitiveProcessTag}
            selectedTags={selectedTags}
            handleDetailsChange={handleDetailsChange}
            selectedTagKey={tagAxis.id}
          />
        </Typography>
      )}
    </Root>
  )
}

interface TagViewProps {
  paper: CrossRefPaper
  handleNext: () => void
  handleDetailsChange: (update: Partial<CrossRefPaper>) => void
  isEdit?: boolean
}

export const TagModelView: React.FC<TagViewProps> = ({ paper, handleNext, handleDetailsChange, isEdit }) => (
  <Root>
    {/* タグ入力初期画面につき説明文を表示 */}
    {!isEdit && (
      <Typography variant="body2" color="primary">
        Select one or more tags from each category. If multiple tags are selected in a single category, the order of
        selection is indicated. Click the help link to see its definition.
      </Typography>
    )}
    {isEdit && (
      <Typography variant="body2" color="primary">
        Tags are shown as grey if they are already in the database. Select any tags to add or remove them.
      </Typography>
    )}

    <Typography variant="subtitle2" align="right">
      <Link onClick={handleNext}>&gt; Skip</Link>
    </Typography>

    <CustomDivider />

    <Selector1Column
      tagAxisId="compMathMethodExperimentalDesign"
      paper={paper}
      handleDetailsChange={handleDetailsChange}
      helpPopoverComponent={
        <HelpPopover>
          <CompMathMethodExperimentalDesignHelpText />
        </HelpPopover>
      }
    />
    <CustomDivider />
    <Selector1Column tagAxisId="compMathMethodDataType" paper={paper} handleDetailsChange={handleDetailsChange} />
    <CustomDivider />

    <Selector1Column tagAxisId="compMathMethodModels" paper={paper} handleDetailsChange={handleDetailsChange} />
    <SupplementBox>Further details about the model can be added in the last step of the registration.</SupplementBox>
    <CustomDivider />

    <Selector1Column
      tagAxisId="unitOfAnalysis"
      paper={paper}
      handleDetailsChange={handleDetailsChange}
      helpPopoverComponent={
        <HelpPopover>
          <UnitOfAnalysisHelpText />
        </HelpPopover>
      }
    />
    {/* eslint-disable-next-line react/no-unescaped-entities */}
    <SupplementBox>
      {/* eslint-disable-next-line react/no-unescaped-entities */}
      If a category has no tags that match the paper, feel free to leave it blank to mark the category as "unspecified".
    </SupplementBox>
    <CustomDivider />
  </Root>
)

export const TagDiseaseView: React.FC<TagViewProps> = ({ paper, handleNext, handleDetailsChange }) => (
  <Root>
    <Typography variant="subtitle2" align="right">
      <Link onClick={handleNext}>&gt; Skip</Link>
    </Typography>
    <CustomDivider />

    <Selector1Column
      tagAxisId="dsm5"
      paper={paper}
      handleDetailsChange={handleDetailsChange}
      helpPopoverComponent={
        <>
          <HelpPopover>
            <Dsm5HelpText />
          </HelpPopover>
          <Typography variant="caption" color="primary" component="span">
            {"<- Can't find a disease category in DSM-5?"}
          </Typography>
        </>
      }
    />
    <CustomDivider />

    <Selector2Column
      tagAxisId="symptomatology"
      paper={paper}
      handleDetailsChange={handleDetailsChange}
      helpPopoverComponent={
        <HelpPopover>
          <SymptomatologyHelpText />
        </HelpPopover>
      }
    />
  </Root>
)

export const TagRdocView: React.FC<
  TagViewProps & {
    handleIsReadySubmit: (isReadySubmit: boolean) => void
  }
> = ({ paper, handleNext, handleDetailsChange, handleIsReadySubmit }) => {
  useUnmount(() => {
    handleIsReadySubmit(false)
  })
  return (
    <Root>
      <Typography variant="subtitle2" align="right">
        <Link onClick={handleNext}>&gt; Skip</Link>
      </Typography>

      <CustomDivider />
      <Selector3Column
        tagAxisId="rdocConstructs"
        paper={paper}
        handleDetailsChange={handleDetailsChange}
        helpPopoverComponent={
          <HelpPopover>
            <RdocConstructsHelpText />
          </HelpPopover>
        }
      />
    </Root>
  )
}
